<template>
  <div class="row push-up">
    <div class="small-12 medium-6 large-6 columns">
      <div class="row">
        <div class="small-6 columns" data-test-body-parts-right v-html="contentModel.PersonRight" />
        <div
          class="small-6 columns text-right"
          data-test-body-parts-left
          v-html="contentModel.PersonLeft"
        />
      </div>
      <div :style="personBackground" class="square body-area">
        <div v-for="(buttonItem, i) in buttonItems" :key="'button-item--' + buttonItem.id">
          <button
            :id="'body-area--' + buttonItem.id"
            :data-test-body-part-category="i + 1"
            :class="[{ selected: getActiveClass(buttonItem) }, 'body-area--' + [i + 1], 'button']"
            :disabled="getDisabledState(buttonItem)"
            :title="buttonItem.description"
            @click="selectButtonItem(buttonItem)"
            v-text="i + 1"
          />

          <body-part-selector
            data-test-body-part-selector
            :active="buttonItem.active"
            :title="buttonItem.description"
            @close="close(buttonItem)"
          >
            <div
              v-for="(bodyPart, a) in buttonItem.items"
              :key="'body-part--' + bodyPart.value"
              class="cell"
            >
              <base-single-checkbox
                v-model="bodyPart.selected"
                :id="bodyPart.value"
                :name="'body-part--' + a"
                :label="bodyPart.label"
                :data-test-input="'body-part-' + (a + 1)"
                @update:model-value="selectBodyPart(buttonItem)"
                :errors="errors"
              />
            </div>
          </body-part-selector>
        </div>
      </div>
    </div>
    <div class="small-12 medium-6 large-6 columns">
      <div class="callout" data-test-selected-body-parts>
        <h3>Valgte legemsdele ({{ selectedItems.length }})</h3>

        <span v-if="selectedItems.length === 0" class="italic">Ingen valgt</span>
        <ul v-else class="no-bullet">
          <li v-for="item in selectedItems" :key="item.id" :data-test-selected-body-part="item.id">
            <div class="row">
              <div class="columns small-10" v-html="item.label" />
              <div class="columns small-2">
                <a
                  data-test-remove-selected-body-part
                  class="icon icon-trash align-right"
                  @click="removeItem(item)"
                />
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { BodyPart, DataModel, ButtonItem, CheckBoxItem } from './PersonalAccidentBodyParts.types';
import BodyPartSelector from './BodyPartSelector.vue';
import Utility from '@/common/UtilityFunctions.js';
import { Step3Accident } from '@/interfaces/CreateClaimFlowViewModel';
import BaseSingleCheckbox from '@/components/Forms/BaseSingleCheckbox.vue';
import { defineComponent, PropType } from 'vue';
import { sortBy } from 'lodash';

export default defineComponent({
  name: 'PersonalAccidentBodyParts',
  components: { BaseSingleCheckbox, BodyPartSelector },
  props: {
    modelValue: {
      type: Array as PropType<BodyPart[] | null>,
      default() {
        return [];
      }
    },
    bodyDamageList: {
      type: Array as PropType<BodyPart[]>,
      default() {
        return [];
      }
    },
    contentModel: {
      type: Object as PropType<Step3Accident>,
      default() {
        return {
          StepTitle: '',
          PersonSecondTitle: '',
          PersonRight: '',
          PersonLeft: '',
          PersonRequiredErrorMessage: '',
          TeethSecondTitle: '',
          TeethUpperMouth: '',
          TeethLowerMouth: '',
          TeethDentistStatementNotice: '',
          TeethRequiredErrorMessage: '',
          LinkPersonalPrivacyURL: '',
          LinkPersonalPrivacyText: ''
        };
      }
    },
    errors: {
      type: Object,
      default() {
        return null;
      }
    }
  },
  data(): DataModel {
    return {
      buttonItems: []
    };
  },
  computed: {
    personBackground(): string {
      return `background-image: url('/assets/portal/images/damagezones/${Utility.getBrandFolderName()}/Person.svg`;
    },
    selectedItems(): CheckBoxItem[] {
      const buttonItemsWithSelections: CheckBoxItem[] = [];

      this.buttonItems.forEach((o: ButtonItem) => {
        const checkBoxItems: CheckBoxItem[] = o.items.filter((a: CheckBoxItem) => a.selected);
        checkBoxItems.forEach((checkBoxItem) => {
          buttonItemsWithSelections.push(checkBoxItem);
        });
      });

      return buttonItemsWithSelections;
    },
    emittableBodyDamageZones(): BodyPart[] {
      const bodyDamageZones: BodyPart[] = [];

      this.selectedItems.forEach((selectedItem) => {
        const bodyPart = this.bodyDamageList.find(
          (bodyDamageListItem) => bodyDamageListItem.Id === selectedItem.id
        );

        bodyDamageZones.push(bodyPart as BodyPart);
      });

      return bodyDamageZones;
    }
  },
  mounted(): void {
    this.buttonItems = this.populateButtonItems();
  },
  methods: {
    populateButtonItems(): ButtonItem[] {
      const sortedArray = sortBy(this.bodyDamageList, (o) => o.SortOrder);
      const buttonItemsMap: Map<string, ButtonItem> = new Map();

      sortedArray.forEach((bodyPart) => {
        const existingItem = buttonItemsMap.get(bodyPart.ItemGroupId);
        const preSelected = this.modelValue?.find((o) => o.Id === bodyPart.Id);

        if (existingItem === undefined) {
          const newButtonItem: ButtonItem = {
            id: bodyPart.ItemGroupId,
            description: bodyPart.ItemGroupDescription,
            active: false,
            selected: false,
            items: [
              {
                id: bodyPart.Id,
                label: bodyPart.Description,
                selected: preSelected !== undefined,
                value: bodyPart.Id
              }
            ]
          };

          buttonItemsMap.set(bodyPart.ItemGroupId, newButtonItem);
        } else {
          existingItem.items.push({
            id: bodyPart.Id,
            label: bodyPart.Description,
            selected: preSelected !== undefined,
            value: bodyPart.Id
          });
        }
      });

      return Array.from(buttonItemsMap.values());
    },
    selectButtonItem(item: ButtonItem): void {
      this.buttonItems.forEach((obj) => {
        if (obj.id !== item.id) {
          obj.active = false;
        }
      });

      item.active = !item.active;
    },
    selectBodyPart(item: ButtonItem): void {
      item.selected = item.items.filter((obj) => obj.selected).length > 0;
      this.$emit('update:modelValue', this.emittableBodyDamageZones);
    },
    getActiveClass(item: ButtonItem): boolean {
      return item.items.filter((o) => o.selected).length > 0 || item.active;
    },
    getDisabledState(item: ButtonItem): boolean {
      if (this.buttonItems.filter((o) => o.active).length === 0) {
        return false;
      }

      if (item.active) {
        return false;
      }

      return true;
    },
    removeItem(item: CheckBoxItem): void {
      item.selected = false;
      this.$emit('update:modelValue', this.emittableBodyDamageZones);
    },
    close(item: ButtonItem): void {
      item.active = false;
    }
  }
});
</script>

<style lang="scss" scoped>
@import 'src/styles/all';

a {
  text-decoration: none;
}
.square {
  position: relative;
  width: 100%;
  padding-bottom: 250%;
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
}

.body-area {
  > div {
    > button {
      position: absolute;
      margin-left: rem-calc(-13.5);
      width: rem-calc(27);
      height: rem-calc(27);
      padding: 0 0 0 0 !important;
      font-size: $small-font-size;
      z-index: 200;
      justify-content: space-around;

      &.selected,
      &:active {
        background-color: $secondary-color;
        opacity: 0.9;
      }

      &.body-area {
        // Psykisk
        &--1 {
          top: 2%;
          left: 50%;
        }

        // Hoved
        &--2 {
          top: 10%;
          left: 50%;
        }

        // Nakke
        &--3 {
          top: 21%;
          left: 50%;
        }

        // Ryg
        &--4 {
          top: 28%;
          left: 50%;
        }

        // Bækken
        &--5 {
          top: 50%;
          left: 50%;
        }

        // Indre organer
        &--6 {
          top: 35%;
          left: 50%;
        }

        // Hofte (højre)
        &--7 {
          top: 50%;
          left: 37%;
        }

        // Hofte (venstre)
        &--8 {
          top: 50%;
          left: 63%;
        }

        // Lår (højre)
        &--9 {
          top: 61%;
          left: 28%;
        }

        // Lår (venstre)
        &--10 {
          top: 61%;
          left: 72%;
        }

        // Knæ (højre)
        &--11 {
          top: 70%;
          left: 30%;
        }

        // Knæ (venstre)
        &--12 {
          top: 70%;
          left: 70%;
        }

        // Underben (højre)
        &--13 {
          top: 80%;
          left: 32%;
        }

        // Underben (venstre)
        &--14 {
          top: 80%;
          left: 68%;
        }

        // Ankel (højre)
        &--15 {
          top: 87%;
          left: 43%;
        }

        // Ankel (venstre)
        &--16 {
          top: 87%;
          left: 57%;
        }

        // Fod (højre)
        &--17 {
          top: 91%;
          left: 35%;
        }

        // Fod (venstre)
        &--18 {
          top: 91%;
          left: 65%;
        }

        // Skulder (højre)
        &--19 {
          top: 23%;
          left: 28%;
        }

        // Skulder (venstre)
        &--20 {
          top: 23%;
          left: 72%;
        }

        // Albue (højre)
        &--21 {
          top: 37%;
          left: 19%;
        }

        // Albue (venstre)
        &--22 {
          top: 37%;
          left: 81%;
        }

        // Underarm (højre)
        &--23 {
          top: 44%;
          left: 19%;
        }

        // Underarm (venstre)
        &--24 {
          top: 44%;
          left: 81%;
        }

        // Hånd (højre)
        &--25 {
          top: 52%;
          left: 21%;
        }

        // Hånd (venstre)
        &--26 {
          top: 52%;
          left: 79%;
        }
      }
    }
  }
}
</style>
@/components/Feature/Forms/BaseSingleCheckbox.vue
